import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { withStyles, createStyles } from '@mui/styles'
import { FormGroup, FormControlLabel, Checkbox } from '@mui/material'
import { muiOptions, MuiProps, defaultStyles, defaultColors } from '../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../infrastructure/i18nextHelper'
import * as api from '../../../infrastructure/api'
import { hasFeature } from '../../../infrastructure/feature'
import { SnackbarContainer } from '../../../infrastructure/snackbars'
import { reportStyles, ReportShell } from './_common'
import { Button, DatePicker, MonthPicker, Select } from '../../common/customComponents'
import { Guid } from '../../../infrastructure/guid'

type PrimoFilename = {
    singleFlowsFilenames: string[]
    aggregatedFlowsFilenames: string[]
}

type StringDate = string

type Product = {
    productId: Guid
    product: string
}

export async function generatePrimoFiles(
    fromMonth: StringDate | null,
    toMonth: StringDate | null,
    forecastStart: StringDate | null,
    withSingleFlows: boolean,
    withAggregatedFlows: boolean,
    customProductId: Guid | null,
    customForecastStartDate: StringDate | null
) {
    let filenames = await api.get<PrimoFilename>('report/primo/filenames')
    let requests = []
    if (withSingleFlows) requests.push(requestSingleFlows())
    if (withAggregatedFlows) requests.push(requestAggregatedFlows())
    await Promise.all(requests)

    async function requestSingleFlows() {
        await api.post('report/primo/singleFlows', { fromMonth, toMonth, forecastStartDate: forecastStart })
        await Promise.all(filenames.singleFlowsFilenames.map(x => api.openDownload(x)))
    }

    async function requestAggregatedFlows() {
        await api.post('report/primo/aggregatedFlows', { fromMonth, toMonth, forecastStartDate: forecastStart, customProductId, customForecastStartDate })
        await Promise.all(filenames.aggregatedFlowsFilenames.map(x => api.openDownload(x)))
    }
}

function _primo({ classes }: MuiProps) {
    let [forecastStart, setForecastStart] = useState<StringDate | null>(null)
    let [fromMonth, setFromMonth] = useState<StringDate | null>(moment().startOf('month').format('YYYY-MM-DD'))
    let [toMonth, setToMonth] = useState<StringDate | null>(moment().add(3, 'months').endOf('month').format('YYYY-MM-DD'))
    let [withSingleFlows, setWithSingleFlows] = useState<boolean>(true)
    let [withAggregatedFlows, setWithAggregatedFlows] = useState<boolean>(true)
    let [products, setProducts] = useState<Product[]>([])
    let [customProductId, setCustomProductId] = useState<Guid | null>(null)
    let [customForecastStart, setCustomForecastStart] = useState<StringDate | null>(null)

    let snackbar = SnackbarContainer.useContainer()

    let generatePrimo = async () => {
        if (!withSingleFlows && !withAggregatedFlows) {
            snackbar.info(t('report.primo.noExportWithoutSelection'))
            return
        }
        await generatePrimoFiles(fromMonth, toMonth, forecastStart, withSingleFlows, withAggregatedFlows, customProductId, customForecastStart);
    }

    let load = async () => {
        let { date } = await api.get<{ date: StringDate }>('stock/movement/mktsale/forecastStart')
        setForecastStart(date)

        let products = await api.get<Product[]>('report/primo/products')
        setProducts(products)
    }

    useEffect(() => { load() }, [])

    useEffect(() => {
        if (!customProductId)
            setCustomForecastStart(null)
    }, [customProductId])

    return (
        <ReportShell title={t('report.primo.name')} displayError={false}>
            <div className={classes.form}>
                <Button className={classes.generateButton}
                    label={t('report.export')}
                    img='/static/images/excel_red.svg'
                    onClick={generatePrimo} />
            </div>
            <div className={classes.dates}>
                <div className={classes.settings}>
                    <FormGroup row>
                        <FormControlLabel
                            control={<Checkbox style={{ color: defaultColors.red.main.color }} checked={withSingleFlows} onChange={() => setWithSingleFlows(!withSingleFlows)} />}
                            label={t('report.primo.withSingleFlows')} />
                        <FormControlLabel
                            control={<Checkbox style={{ color: defaultColors.red.main.color }} checked={withAggregatedFlows} onChange={() => setWithAggregatedFlows(!withAggregatedFlows)} />}
                            label={t('report.primo.withAggregatedFlows')} />
                    </FormGroup>
                    {hasFeature('MarketRiskPastForecast') &&
                        <DatePicker classesOverride={{ datepicker: classes.datePicker }} date={forecastStart}
                            label={t('report.forecastStart')} setDate={newDate => setForecastStart(newDate)} />
                    }
                    <MonthPicker classesOverride={{ datepicker: classes.datePicker }} date={fromMonth}
                        label={t('report.fromMonth')} setDate={setFromMonth} />
                    <MonthPicker classesOverride={{ datepicker: classes.datePicker }} date={toMonth}
                        label={t('report.toMonth')} setDate={setToMonth} />
                </div>
                {hasFeature('MarketRiskPastForecast') &&
                    <div className={classes.customSettings}>
                        <Select label={t('report.primo.products')}
                            isClearable={true}
                            clearableLabel='-'
                            value={customProductId}
                            onChange={val => setCustomProductId(val)}
                            choices={products.map(x => ({ value: x.productId, text: x.product }))} />
                        <DatePicker classesOverride={{ datepicker: classes.datePicker }} date={customForecastStart}
                            label={t('report.forecastStart')} setDate={newDate => setCustomForecastStart(newDate)} />
                    </div>
                }
            </div>
        </ReportShell>
    )
}

let styles = theme => createStyles({
    generateButton: defaultStyles.secondaryButton,
    datePicker: {
        marginLeft: '1em'
    },
    form: reportStyles.form,
    selectors: defaultStyles.flexColumn,
    dates: {
        ...defaultStyles.flexColumn,
        width: '80%',
        alignItems: 'flex-start'
    },
    customSettings: {
        display: 'flex',
        flexDirection: 'row',
        gap: '0.5em',
        marginTop: '2em'
    },
    settings: {
        display: 'flex',
        flexDirection: 'row',
        gap: '0.5em'
    }
})

export default withStyles(styles, muiOptions)(_primo)